/**
 * useFileUpload Hook
 *
 * Manages the lifecycle of file attachments:
 * client-side validation → upload intent → storage upload → complete
 */

import { useState, useCallback, useMemo } from 'react';
import {
  createUploadIntent,
  uploadFileToStorage,
  completeUpload,
} from '../services/upload-service';

// ============================================================================
// Constants (mirrors backend ALLOWED_TYPES / UPLOAD_LIMITS)
// ============================================================================

const ALLOWED_MIME_TYPES: Record<string, { maxBytes: number; category: string }> = {
  'image/png': { maxBytes: 20 * 1024 * 1024, category: 'image' },
  'image/jpeg': { maxBytes: 20 * 1024 * 1024, category: 'image' },
  'image/gif': { maxBytes: 10 * 1024 * 1024, category: 'image' },
  'image/webp': { maxBytes: 20 * 1024 * 1024, category: 'image' },
  'application/pdf': { maxBytes: 50 * 1024 * 1024, category: 'document' },
  'application/msword': { maxBytes: 25 * 1024 * 1024, category: 'document' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    maxBytes: 25 * 1024 * 1024,
    category: 'document',
  },
  'text/plain': { maxBytes: 5 * 1024 * 1024, category: 'document' },
  'text/csv': { maxBytes: 10 * 1024 * 1024, category: 'spreadsheet' },
  'application/vnd.ms-excel': { maxBytes: 25 * 1024 * 1024, category: 'spreadsheet' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    maxBytes: 25 * 1024 * 1024,
    category: 'spreadsheet',
  },
};

const DEFAULT_MAX_FILES = 10;
const MAX_TOTAL_BYTES = 100 * 1024 * 1024;

export const ACCEPT_STRING = Object.keys(ALLOWED_MIME_TYPES).join(',');

// ============================================================================
// Types
// ============================================================================

export type AttachmentStatus = 'uploading' | 'ready' | 'failed';

export interface AttachmentState {
  id: string;
  file: File;
  tempUploadId?: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: AttachmentStatus;
  progress: number;
  errorMessage?: string;
  thumbnailUrl?: string;
}

export interface UseFileUploadOptions {
  maxFiles?: number;
}

export interface UseFileUploadReturn {
  attachments: AttachmentState[];
  addFiles: (files: FileList | File[]) => void;
  removeAttachment: (id: string) => void;
  retryFile: (id: string) => void;
  clearAll: () => void;
  hasUploading: boolean;
  hasFailed: boolean;
  canSubmit: boolean;
  readyTempUploadIds: string[];
}

// ============================================================================
// Hook
// ============================================================================

let nextId = 0;

export function useFileUpload(options?: UseFileUploadOptions): UseFileUploadReturn {
  const maxFiles = options?.maxFiles ?? DEFAULT_MAX_FILES;
  const [attachments, setAttachments] = useState<AttachmentState[]>([]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);

    setAttachments((prev) => {
      // Check max files
      const remaining = maxFiles - prev.length;
      if (remaining <= 0) return prev;
      const toAdd = fileArray.slice(0, remaining);

      // Check total size
      const currentTotal = prev.reduce((sum, a) => sum + a.sizeBytes, 0);
      const filtered: File[] = [];
      let runningTotal = currentTotal;
      for (const f of toAdd) {
        if (runningTotal + f.size > MAX_TOTAL_BYTES) break;
        runningTotal += f.size;
        filtered.push(f);
      }

      const newAttachments: AttachmentState[] = filtered.map((file) => {
        const id = `att_${++nextId}`;

        // Client-side validation
        const typeConfig = ALLOWED_MIME_TYPES[file.type];
        if (!typeConfig) {
          return {
            id,
            file,
            fileName: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            status: 'failed' as const,
            progress: 0,
            errorMessage: `Unsupported file type: ${file.type || 'unknown'}`,
          };
        }

        if (file.size > typeConfig.maxBytes) {
          return {
            id,
            file,
            fileName: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            status: 'failed' as const,
            progress: 0,
            errorMessage: `File too large (max ${Math.round(typeConfig.maxBytes / 1024 / 1024)}MB)`,
          };
        }

        // Create thumbnail for images
        const thumbnailUrl = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : undefined;

        const attachment: AttachmentState = {
          id,
          file,
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          status: 'uploading',
          progress: 0,
          thumbnailUrl,
        };

        // Start async upload
        uploadFile(id, file);

        return attachment;
      });

      return [...prev, ...newAttachments];
    });
  }, [maxFiles]);

  const uploadFile = async (attachmentId: string, file: File) => {
    try {
      // Step 1: Create intent
      const intent = await createUploadIntent(file.name, file.type, file.size);

      setAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId
            ? { ...a, tempUploadId: intent.tempUploadId, progress: 5 }
            : a
        )
      );

      // Step 2: Upload to storage via Firebase SDK
      await uploadFileToStorage(intent.storagePath, file, (percent) => {
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === attachmentId
              ? { ...a, progress: 5 + Math.round(percent * 0.9) }
              : a
          )
        );
      });

      // Step 3: Complete
      await completeUpload(intent.tempUploadId);

      setAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId
            ? { ...a, status: 'ready' as const, progress: 100 }
            : a
        )
      );
    } catch (error) {
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId
            ? {
                ...a,
                status: 'failed' as const,
                errorMessage:
                  error instanceof Error ? error.message : 'Upload failed',
              }
            : a
        )
      );
    }
  };

  const retryFile = useCallback((id: string) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (!att || att.status !== 'failed') return prev;

      const file = att.file;
      // Remove the failed attachment and re-queue
      const without = prev.filter((a) => a.id !== id);
      const newId = `att_${++nextId}`;

      const typeConfig = ALLOWED_MIME_TYPES[file.type];
      if (!typeConfig) return without; // still unsupported

      const thumbnailUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;

      const newAtt: AttachmentState = {
        id: newId,
        file,
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        status: 'uploading',
        progress: 0,
        thumbnailUrl,
      };

      uploadFile(newId, file);
      return [...without, newAtt];
    });
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.thumbnailUrl) {
        URL.revokeObjectURL(att.thumbnailUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((a) => {
        if (a.thumbnailUrl) URL.revokeObjectURL(a.thumbnailUrl);
      });
      return [];
    });
  }, []);

  const hasUploading = useMemo(
    () => attachments.some((a) => a.status === 'uploading'),
    [attachments]
  );

  const hasFailed = useMemo(
    () => attachments.some((a) => a.status === 'failed'),
    [attachments]
  );

  const canSubmit = useMemo(
    () => !hasUploading && !hasFailed,
    [hasUploading, hasFailed]
  );

  const readyTempUploadIds = useMemo(
    () =>
      attachments
        .filter((a) => a.status === 'ready' && a.tempUploadId)
        .map((a) => a.tempUploadId!),
    [attachments]
  );

  return {
    attachments,
    addFiles,
    removeAttachment,
    retryFile,
    clearAll,
    hasUploading,
    hasFailed,
    canSubmit,
    readyTempUploadIds,
  };
}
